import React, { useMemo } from 'react'
import { Checkbox, Col, Flex, Form, GetProp, Input, Row, Select, Typography, Upload } from 'antd'
import { ErrorMessage } from '@hookform/error-message'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PathIcon } from 'src/assets/icons'
import { RcFile, UploadProps } from 'antd/es/upload'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import SkipRowsRangeField from '../skip-rows-range-field'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface Props {
  errors: any
  control: any
  setValue: any
  trigger: any
  clearErrors: any
  onSkipRowsChange: () => void
  beforeUpload: (file: RcFile, FileList: RcFile[]) => any | Promise<any>
  file?: FileType
  formatType: string
  resetUploadFile: () => void
  setIsChanged?: (value: boolean) => void
}

function UploadForm(props: Readonly<Props>) {
  const { t } = useTranslation()
  const lookupValues = useSelector(getLookupValues)

  const { FormatType, TextQualifier } = lookupValues || {}

  const {
    errors,
    control,
    setValue,
    trigger,
    clearErrors,
    onSkipRowsChange,
    beforeUpload,
    formatType,
    resetUploadFile,
    setIsChanged,
  } = props

  const uploadFormat = useMemo(() => {
    if (formatType === 'csv') return '.csv'
    if (formatType === 'excel') return '.xlsx ,.xls'
    if (formatType === 'txt') return 'text/plain'
  }, [formatType])

  return (
    <Flex vertical>
      <Row gutter={24} align='middle'>
        <Col span={24} md={12}>
          <Form.Item label={t('FileMappingSetup:addFileMapping.label.formatName')} className='isRequired'>
            <Controller
              name='formatName'
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  placeholder={t('FileMappingSetup:addFileMapping.placeholder.formatName')}
                  onBlur={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                  maxLength={50}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='formatName' />
            </Typography>
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item label={t('FileMappingSetup:addFileMapping.label.formatType')} className='isRequired'>
            <Controller
              name='formatType'
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  options={FormatType}
                  placeholder={t('FileMappingSetup:addFileMapping.placeholder.formatType')}
                  onChange={(e) => {
                    setIsChanged && setIsChanged(true)
                    field?.onChange(e)
                    resetUploadFile()
                  }}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='formatType' />
            </Typography>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24} align='middle'>
        <Col span={24} md={12}>
          <Form.Item valuePropName='checked'>
            <Controller
              name='fileContainsIssuedDate'
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(e) => {
                    setIsChanged && setIsChanged(true)
                    field?.onChange(e)
                    resetUploadFile()
                  }}
                >
                  {t('FileMappingSetup:addFileMapping.label.fileContainsIssuedDate')}
                </Checkbox>
              )}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Flex align='center' gap={16} wrap='wrap' className='skip-rows-range-group'>
            <Form.Item valuePropName='checked'>
              <Controller
                name='fileContainsHeaderRow'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      setIsChanged && setIsChanged(true)
                      field?.onChange(e)
                      resetUploadFile()
                    }}
                  >
                    {t('FileMappingSetup:addFileMapping.label.fileContainsHeaderRow')}
                  </Checkbox>
                )}
              />
            </Form.Item>

            <SkipRowsRangeField
              errors={errors}
              control={control}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
              onRangeChange={onSkipRowsChange}
            />
          </Flex>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24} md={12}>
          <Form.Item label={t('FileMappingSetup:addFileMapping.label.textQualifier')}>
            <Controller
              name='textQualifier'
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  placeholder={t('FileMappingSetup:addFileMapping.placeholder.textQualifier')}
                  options={TextQualifier}
                  onChange={(e) => {
                    setIsChanged && setIsChanged(true)
                    field?.onChange(e)
                    resetUploadFile()
                  }}
                />
              )}
              disabled={!['.csv'].includes(uploadFormat as string)}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='textQualifier' />
            </Typography>
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item label={t('FileMappingSetup:addFileMapping.label.sampleFile')} className='isRequired'>
            <Controller
              name='sampleFile'
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  className='sample-file-upload'
                  placeholder={t('FileMappingSetup:addFileMapping.placeholder.sampleFile')}
                  prefix={<PathIcon />}
                  suffix={
                    <Upload
                      accept={uploadFormat}
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      className='upload-button'
                    >
                      {t('OverallFunctionality:select_btn')}
                    </Upload>
                  }
                  readOnly
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='sampleFile' />
            </Typography>
          </Form.Item>
        </Col>
      </Row>
    </Flex>
  )
}

export default UploadForm
